import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Dropdown from "../ui/Dropdown";

const AddToLeagueModal = ({ isOpen, onClose, user, onAddToLeague }) => {
  const [selectedLeague, setSelectedLeague] = useState("");
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchAvailableLeagues = useCallback(async () => {
    if (!user || !user.id) return;
    try {
      setLoading(true);
      setError("");

      // Get all leagues
      const allLeaguesResponse = await axios.get(`${API_URL}/leagues`);
      const allLeagues = allLeaguesResponse.data.leagues || [];

      // Get user's current leagues
      const userLeaguesResponse = await axios.get(
        `${API_URL}/users/${user.id}/leagues`,
      );
      const userLeagues = userLeaguesResponse.data.leagues || [];
      const userLeagueIds = userLeagues.map((league) => league.id);

      // Filter out leagues user is already in
      const available = allLeagues.filter(
        (league) => !userLeagueIds.includes(league.id),
      );

      const leagueOptions = available.map((league) => ({
        value: league.id,
        label: league.name,
      }));

      setAvailableLeagues(leagueOptions);
    } catch (err) {
      setError("Failed to fetch available leagues");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAvailableLeagues();
  }, [fetchAvailableLeagues]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLeague || !user || !user.id) {
      setError("Please select a league");
      return;
    }

    setLoading(true);
    try {
      await onAddToLeague(user.id, selectedLeague);
      setSelectedLeague("");
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.error?.message || "Failed to add user to league",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedLeague("");
    setError("");
    onClose();
  };

  if (!user || !user.id) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Add User to League">
        <div className="p-6 text-center text-gray-500">Loading user...</div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add User to League">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Add <strong>{user?.name}</strong> to a league
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <Dropdown
            label="Select League"
            value={selectedLeague}
            onChange={setSelectedLeague}
            options={availableLeagues}
            placeholder="Choose a league..."
          />
        </div>

        {availableLeagues.length === 0 && !loading && (
          <div className="text-sm text-gray-500 text-center py-4">
            No available leagues. User is already in all leagues.
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              loading || !selectedLeague || availableLeagues.length === 0
            }
          >
            {loading ? "Adding..." : "Add to League"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddToLeagueModal;
